import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Save, Sparkles, UserCircle, Trash2 } from "lucide-react";

const empty = { positioning: "", target_audience: "", tone: "", pillars: [""], example_posts: [""], author_name: "", portrait_data: null };

export default function Onboarding() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/style-profile").then((res) => {
      if (res.data) {
        setForm({
          positioning: res.data.positioning || "",
          target_audience: res.data.target_audience || "",
          tone: res.data.tone || "",
          pillars: res.data.pillars?.length ? res.data.pillars : [""],
          example_posts: res.data.example_posts?.length ? res.data.example_posts : [""],
          author_name: res.data.author_name || "",
          portrait_data: res.data.portrait_data || null,
        });
      }
    }).finally(() => setLoaded(true));
  }, []);

  const updateList = (key, i, val) => {
    const arr = [...form[key]];
    arr[i] = val;
    setForm({ ...form, [key]: arr });
  };
  const addItem = (key) => setForm({ ...form, [key]: [...form[key], ""] });
  const removeItem = (key, i) => setForm({ ...form, [key]: form[key].filter((_, idx) => idx !== i) });

  const pickPortrait = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Photo trop lourde (max 4 Mo)."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, portrait_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/style-profile", {
        positioning: form.positioning,
        target_audience: form.target_audience,
        tone: form.tone,
        pillars: form.pillars.map((p) => p.trim()).filter(Boolean),
        example_posts: form.example_posts.map((p) => p.trim()).filter(Boolean),
        author_name: form.author_name,
        portrait_data: form.portrait_data,
      });
      toast.success("Profil de style enregistré. Vos prochains posts seront calibrés sur votre voix.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="p-12 text-slate-400">Chargement…</div>;

  const inputCls = "w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors bg-white";
  const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-slate-500";

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Onboarding éditorial</span>
      </div>
      <h1 className="font-serif text-4xl text-slate-900 mb-3">Votre profil de style</h1>
      <p className="text-slate-500 mb-10 max-w-2xl leading-relaxed">
        Plus votre profil est précis, plus l'IA écrit dans votre voix. Décrivez votre positionnement et collez
        5 à 10 de vos meilleurs posts : ils serviront de référence à chaque génération.
      </p>

      <div className="space-y-8">
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
          <div>
            <label className={labelCls}>Positionnement</label>
            <textarea data-testid="positioning-input" rows={3} value={form.positioning}
              onChange={(e) => setForm({ ...form, positioning: e.target.value })}
              className={`${inputCls} mt-1.5 resize-none`}
              placeholder="Ex : Dirigeant d'un cabinet de conseil en transformation, je défends une approche humaine et pragmatique du changement." />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Audience cible</label>
              <input data-testid="audience-input" value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                className={`${inputCls} mt-1.5`} placeholder="Ex : dirigeants de PME, DRH, entrepreneurs" />
            </div>
            <div>
              <label className={labelCls}>Ton</label>
              <input data-testid="tone-input" value={form.tone}
                onChange={(e) => setForm({ ...form, tone: e.target.value })}
                className={`${inputCls} mt-1.5`} placeholder="Ex : direct, incarné, un brin provocateur mais bienveillant" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <label className={labelCls}>Signature visuelle (pour les gabarits d'images)</label>
          <div className="grid md:grid-cols-[1fr_auto] gap-6 mt-3 items-start">
            <div>
              <input data-testid="author-name-input" value={form.author_name}
                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                className={inputCls} placeholder="Nom affiché sur les visuels, ex : Gilles IACONES" />
              <p className="text-xs text-slate-400 mt-2">Ce nom et cette photo sont injectés automatiquement dans vos visuels de marque.</p>
            </div>
            <div className="flex items-center gap-3">
              {form.portrait_data ? (
                <div className="relative">
                  <img src={form.portrait_data} alt="portrait" className="w-24 h-24 object-cover rounded-sm border border-slate-200" />
                  <button data-testid="remove-portrait-btn" onClick={() => setForm({ ...form, portrait_data: null })}
                    className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-500 hover:text-destructive transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <label data-testid="portrait-upload-label" className="w-24 h-24 flex flex-col items-center justify-center gap-1 border border-dashed border-slate-300 rounded-sm text-slate-400 cursor-pointer hover:border-coral hover:text-coral-dark transition-colors text-[11px]">
                  <UserCircle className="h-6 w-6" /> Photo
                  <input data-testid="portrait-upload-input" type="file" accept="image/*" onChange={pickPortrait} className="hidden" />
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <label className={labelCls}>Piliers de contenu (3 à 5)</label>
          <div className="space-y-2.5 mt-3">
            {form.pillars.map((p, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`pillar-input-${i}`} value={p}
                  onChange={(e) => updateList("pillars", i, e.target.value)}
                  className={inputCls} placeholder={`Pilier ${i + 1}, ex : leadership, culture d'entreprise…`} />
                {form.pillars.length > 1 && (
                  <button data-testid={`remove-pillar-${i}`} onClick={() => removeItem("pillars", i)}
                    className="px-3 border border-slate-200 rounded-sm text-slate-400 hover:text-destructive hover:border-destructive transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button data-testid="add-pillar-btn" onClick={() => addItem("pillars")}
            className="mt-3 text-sm text-coral-dark font-semibold flex items-center gap-1.5 hover:underline">
            <Plus className="h-4 w-4" /> Ajouter un pilier
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <label className={labelCls}>Posts de référence (5 à 10 idéalement)</label>
          <div className="space-y-3 mt-3">
            {form.example_posts.map((p, i) => (
              <div key={i} className="flex gap-2">
                <textarea data-testid={`example-post-${i}`} rows={4} value={p}
                  onChange={(e) => updateList("example_posts", i, e.target.value)}
                  className={`${inputCls} resize-none font-serif text-[15px] leading-relaxed`}
                  placeholder={`Collez ici votre post #${i + 1}…`} />
                {form.example_posts.length > 1 && (
                  <button data-testid={`remove-example-${i}`} onClick={() => removeItem("example_posts", i)}
                    className="px-3 border border-slate-200 rounded-sm text-slate-400 hover:text-destructive hover:border-destructive transition-colors self-start py-2.5">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button data-testid="add-example-btn" onClick={() => addItem("example_posts")}
            className="mt-3 text-sm text-coral-dark font-semibold flex items-center gap-1.5 hover:underline">
            <Plus className="h-4 w-4" /> Ajouter un post
          </button>
        </div>

        <div className="flex items-center gap-4 sticky bottom-0 bg-background/80 backdrop-blur-sm py-4">
          <button data-testid="save-profile-btn" onClick={save} disabled={busy}
            className="bg-coral-strong text-white px-6 py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60 flex items-center gap-2">
            <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : "Enregistrer le profil"}
          </button>
        </div>
      </div>
    </div>
  );
}
